// BaseFilterDropdown - Reusable filter dropdown component
// CREATED: Unified base component for all filter dropdowns (Site, Service, Team)
// Features: Optional search bar, "Select All", "Clear All" button, single/multi selection
// Supports both compact and full modes, with responsive BottomSheet on mobile
// Uses render props pattern for customizable option rendering

import { useState, useRef, useEffect, ReactNode } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import { BottomSheet } from './BottomSheet';

interface BaseFilterDropdownProps<T> {
  // Data
  items: T[];
  selectedItems: string[]; // IDs of selected items

  // Callbacks
  onSelectionChange: (itemIds: string[]) => void;
  getItemId: (item: T) => string;
  getItemLabel: (item: T) => string;

  // Display
  label: string;
  icon?: ReactNode;
  placeholder?: string;
  emptyMessage?: string;

  // Behavior
  singleSelection?: boolean;
  compact?: boolean;

  // Features
  searchable?: boolean;
  searchPlaceholder?: string;
  showSelectAll?: boolean;
  showFooter?: boolean;

  // Custom rendering - allows full customization of each option
  renderOption?: (item: T, isSelected: boolean) => ReactNode;

  // Search filter function
  filterItem?: (item: T, query: string) => boolean;
}

export function BaseFilterDropdown<T>({
  items,
  selectedItems,
  onSelectionChange,
  getItemId,
  getItemLabel,
  label,
  icon,
  placeholder = 'Sélectionner...',
  emptyMessage = 'Aucun élément disponible',
  singleSelection = false,
  compact = false,
  searchable = false,
  searchPlaceholder = 'Rechercher...',
  showSelectAll = false,
  showFooter = false,
  renderOption,
  filterItem
}: BaseFilterDropdownProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { isMobile } = useResponsiveLayout();

  // Close on click outside (only for desktop dropdown, not mobile BottomSheet)
  useEffect(() => {
    const isUsingBottomSheet = compact && isMobile;

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    if (isOpen && !isUsingBottomSheet) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, compact, isMobile]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen, searchable]);

  // Filter items based on search query
  const filteredItems = searchable && searchQuery
    ? items.filter(item => {
        if (filterItem) {
          return filterItem(item, searchQuery);
        }
        // Default: search in label
        return getItemLabel(item).toLowerCase().includes(searchQuery.toLowerCase());
      })
    : items;

  const handleToggleItem = (itemId: string) => {
    if (singleSelection) {
      onSelectionChange([itemId]);
      setIsOpen(false);
      setSearchQuery('');
    } else {
      const newSelection = selectedItems.includes(itemId)
        ? selectedItems.filter(id => id !== itemId)
        : [...selectedItems, itemId];
      onSelectionChange(newSelection);
    }
  };

  const handleSelectAll = () => {
    if (selectedItems.length === items.length) {
      onSelectionChange([]);
    } else {
      onSelectionChange(items.map(item => getItemId(item)));
    }
  };

  const handleClearAll = () => {
    onSelectionChange([]);
    setSearchQuery('');
  };

  const getSelectionLabel = (): string => {
    if (selectedItems.length === 0) {
      return singleSelection ? placeholder : `Aucun ${label.toLowerCase()}`;
    }
    if (selectedItems.length === items.length && !singleSelection) {
      return `Tous les ${label.toLowerCase()}`;
    }
    if (selectedItems.length === 1) {
      const item = items.find(i => getItemId(i) === selectedItems[0]);
      return item ? getItemLabel(item) : `1 ${label.toLowerCase()}`;
    }
    return `${selectedItems.length} ${label.toLowerCase()}`;
  };

  const getCompactLabel = (): string => {
    if (selectedItems.length === 0) {
      return label;
    }
    if (selectedItems.length === 1) {
      const item = items.find(i => getItemId(i) === selectedItems[0]);
      return item ? getItemLabel(item) : label;
    }
    return label;
  };

  // Render content for both dropdown and bottom sheet
  const renderContent = () => (
    <>
      {/* Search bar */}
      {searchable && (
        <div className="px-4 py-3 border-b border-gray-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>
      )}

      {/* Header with Clear All button */}
      {!compact && selectedItems.length > 0 && (
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <span className="text-sm font-semibold text-gray-700">{label}</span>
          <button
            onClick={handleClearAll}
            className="text-xs text-primary-600 hover:text-primary-700 font-medium"
          >
            Tout effacer
          </button>
        </div>
      )}

      {/* Select All Option (only in multi-selection mode) */}
      {showSelectAll && !singleSelection && (
        <div className={`px-6 py-3.5 ${!compact && selectedItems.length > 0 ? '' : 'border-b'} border-gray-100`}>
          <label className="flex items-center gap-3 hover:bg-gray-50 cursor-pointer transition-colors py-1 min-h-[48px]">
            <input
              type="checkbox"
              checked={selectedItems.length === items.length}
              onChange={handleSelectAll}
              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700 font-medium">
              Sélectionner tout
            </span>
          </label>
        </div>
      )}

      {/* Items list */}
      {filteredItems.length === 0 ? (
        <div className="px-6 py-3.5 text-center text-gray-500 text-sm">
          {searchQuery ? 'Aucun résultat trouvé' : emptyMessage}
        </div>
      ) : (
        filteredItems.map(item => {
          const itemId = getItemId(item);
          const isSelected = selectedItems.includes(itemId);

          return (
            <label
              key={itemId}
              className="flex items-center gap-3 px-6 py-3.5 min-h-[48px] hover:bg-gray-50 cursor-pointer transition-colors"
            >
              <input
                type={singleSelection ? "radio" : "checkbox"}
                checked={isSelected}
                onChange={() => handleToggleItem(itemId)}
                className={`w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500 ${
                  singleSelection ? 'rounded-full' : 'rounded'
                }`}
              />
              {renderOption ? (
                renderOption(item, isSelected)
              ) : (
                <span className="text-sm text-gray-700 truncate flex-1">
                  {getItemLabel(item)}
                </span>
              )}
            </label>
          );
        })
      )}

      {/* Footer with selected count */}
      {showFooter && selectedItems.length > 0 && (
        <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50">
          <span className="text-xs text-gray-600">
            {getSelectionLabel()}
          </span>
        </div>
      )}
    </>
  );

  if (compact) {
    const hasSelection = selectedItems.length > 0;

    return (
      <div ref={dropdownRef} className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`flex items-center gap-2 px-4 py-2.5 min-h-[46px] border rounded-lg transition-all text-sm font-medium ${
            hasSelection
              ? 'bg-primary-50 border-primary-300 text-primary-700 hover:bg-primary-100'
              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
          }`}
        >
          {icon}
          <span>{getCompactLabel()}</span>
          {selectedItems.length > 1 && (
            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-primary-600 text-white text-xs font-semibold">
              {selectedItems.length}
            </span>
          )}
          <ChevronDown size={16} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {/* Use BottomSheet on mobile, Dropdown on desktop */}
        {isMobile ? (
          <BottomSheet
            isOpen={isOpen}
            onClose={() => {
              setIsOpen(false);
              setSearchQuery('');
            }}
            title={singleSelection ? `Sélectionner un ${label.toLowerCase()}` : `Sélectionner des ${label.toLowerCase()}`}
          >
            {/* Clear All button in BottomSheet header */}
            {selectedItems.length > 0 && (
              <div className="flex items-center justify-end px-4 py-2 border-b border-gray-100 bg-gray-50">
                <button
                  onClick={handleClearAll}
                  className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                >
                  Tout effacer
                </button>
              </div>
            )}
            {renderContent()}
          </BottomSheet>
        ) : (
          isOpen && (
            <div className="absolute z-50 mt-2 left-0 min-w-[280px] bg-white border border-gray-200 rounded-lg shadow-lg overflow-y-auto">
              <div className="max-h-96 py-2">
                {/* Clear All button in dropdown header */}
                {selectedItems.length > 0 && (
                  <div className="flex items-center justify-end px-4 py-2 border-b border-gray-100">
                    <button
                      onClick={handleClearAll}
                      className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                    >
                      Tout effacer
                    </button>
                  </div>
                )}
                {renderContent()}
              </div>
            </div>
          )
        )}
      </div>
    );
  }

  return (
    <div ref={dropdownRef} className="relative">
      <label className="block text-xs font-medium text-gray-600 mb-2">
        {label}
      </label>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-left text-gray-900 font-medium focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent flex items-center justify-between"
      >
        <span className={selectedItems.length === 0 ? 'text-gray-500' : ''}>
          {getSelectionLabel()}
        </span>
        <ChevronDown
          size={20}
          className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-y-auto">
          <div className="max-h-96 py-2">
            {renderContent()}
          </div>
        </div>
      )}
    </div>
  );
}
